/**
 * @file button.tsx
 *
 * MD3 Expressive Button component.
 */

import { Slot } from "@radix-ui/react-slot";
import type { Transition } from "motion/react";
import {
	AnimatePresence,
	animate,
	domMax,
	LazyMotion,
	m,
	useMotionValue,
} from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { LoadingIndicator } from "../../loading-indicator";
import { ProgressIndicator } from "../../progress-indicator";
import { Ripple, useRippleState } from "../../ripple";
import { SPRING_TRANSITION } from "../../shared/constants";
import { TouchTarget } from "../../shared/touch-target";
import {
	BUTTON_COLOR_TOKENS,
	BUTTON_RADIUS_SPRING,
	BUTTON_SHAPE_MORPH_SPRING,
	BUTTON_SIZE_TOKENS,
	BUTTON_TYPOGRAPHY_TOKENS,
} from "./button-tokens";
import type { ButtonProps } from "./types";

const MOTION_PROP_KEYS = [
	"animate",
	"exit",
	"initial",
	"transition",
	"variants",
	"whileHover",
	"whileTap",
	"whileFocus",
	"whileDrag",
	"whileInView",
	"onAnimationStart",
	"onAnimationComplete",
	"onUpdate",
	"onDragStart",
	"onDragEnd",
	"onDrag",
	"onDirectionLock",
	"onDragTransitionEnd",
	"layout",
	"layoutId",
	"onLayoutAnimationComplete",
] as const;

function springAnimate(
	value: ReturnType<typeof useMotionValue<number>>,
	to: number,
) {
	return animate(value, to, { ...BUTTON_RADIUS_SPRING, type: "spring" });
}

interface AnimatedIconSlotProps {
	iconSize: string;
	children: React.ReactNode;
	ariaHidden?: boolean;
	transition?: Transition;
}

function AnimatedIconSlot({
	iconSize,
	children,
	ariaHidden,
	transition,
}: AnimatedIconSlotProps) {
	return (
		<m.span
			layout="position"
			initial={{ width: 0, opacity: 0, scale: 0.5 }}
			animate={{ width: iconSize, opacity: 1, scale: 1 }}
			exit={{ width: 0, opacity: 0, scale: 0.5 }}
			transition={transition}
			aria-hidden={ariaHidden ? "true" : undefined}
			style={{ height: iconSize }}
			className={cn(
				"flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full overflow-hidden",
			)}
		>
			{children}
		</m.span>
	);
}

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			style,
			variant = "default",
			colorStyle = "filled",
			selectedColorStyle: _selectedColorStyle, // Deprecated — destructured to strip from restProps
			size = "sm",
			shape = "round",
			selected,
			icon,
			selectedIcon,
			iconPosition = "leading",
			loading = false,
			loadingVariant = "loading-indicator",
			asChild = false,
			fullWidth = false,
			outlineWidth,
			children,
			onClick,
			onKeyDown,
			"aria-label": ariaLabelProp,
			transition: customTransition,
			...restProps
		},
		ref,
	) => {
		const isToggle = variant === "toggle";
		const isSelected = isToggle ? !!selected : false;

		const effectiveShape = isSelected
			? shape === "round"
				? "square"
				: "round"
			: shape;

		const sizeTokens = BUTTON_SIZE_TOKENS[size] ?? BUTTON_SIZE_TOKENS.sm;
		const animateRadius =
			effectiveShape === "round"
				? sizeTokens.roundShapeRadius
				: sizeTokens.squareShapeRadius;
		const pressedRadius = sizeTokens.pressedShapeRadius;

		const colorState = isToggle
			? isSelected
				? "selected"
				: "unselected"
			: "default";
		const colorClass = BUTTON_COLOR_TOKENS[colorStyle][colorState];
		const typographyClass =
			BUTTON_TYPOGRAPHY_TOKENS[size] ?? BUTTON_TYPOGRAPHY_TOKENS.sm;

		const mergedStyle = {
			height: sizeTokens.height,
			paddingInlineStart: sizeTokens.leadingSpace,
			paddingInlineEnd: sizeTokens.trailingSpace,
			gap: sizeTokens.iconLabelSpace,
			borderWidth:
				outlineWidth ??
				(colorStyle === "outlined" ? sizeTokens.outlineWidth : 0),
			borderStyle: colorStyle === "outlined" ? "solid" : undefined,
			...style,
		};

		const labelText = React.useMemo(() => {
			if (asChild) {
				const child = React.Children.only(children) as React.ReactElement<{
					children?: React.ReactNode;
				}>;
				return child.props.children;
			}
			return children;
		}, [children, asChild]);
		const computedAriaLabel =
			ariaLabelProp || (typeof children === "string" ? children : undefined);
		const needsTouchTarget = size === "xs" || size === "sm";

		const motionRadius = useMotionValue<number>(animateRadius);
		const asChildRef = React.useRef<HTMLElement | null>(null);

		const mergedRef = React.useCallback(
			(node: HTMLElement | null) => {
				asChildRef.current = node;
				if (typeof ref === "function") ref(node as HTMLButtonElement);
				else if (ref)
					(ref as React.MutableRefObject<HTMLButtonElement | null>).current =
						node as HTMLButtonElement;
			},
			[ref],
		);

		React.useEffect(() => {
			if (!asChild) return;
			return motionRadius.on("change", (v) => {
				if (asChildRef.current)
					asChildRef.current.style.borderRadius = `${v}px`;
			});
		}, [motionRadius, asChild]);

		React.useEffect(() => {
			if (!asChild) return;
			const controls = springAnimate(motionRadius, animateRadius);
			return () => controls.stop();
		}, [animateRadius, motionRadius, asChild]);

		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled: loading,
		});

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (loading) return e.preventDefault();
				onClick?.(e);
			},
			[loading, onClick],
		);

		const handleKeyDown = React.useCallback(
			(e: React.KeyboardEvent<HTMLButtonElement>) => {
				if (loading) return;
				if (onClick && (e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					(e.currentTarget as HTMLButtonElement).click();
				}
				onKeyDown?.(e);
			},
			[loading, onClick, onKeyDown],
		);

		const buttonClassName = cn(
			"relative inline-flex flex-row items-center justify-center",
			fullWidth ? "w-full" : "w-fit shrink-0",
			"whitespace-nowrap select-none cursor-pointer",
			"transition-[background-color,color,border-color,box-shadow,opacity,filter] duration-200",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-2",
			"disabled:pointer-events-none disabled:opacity-[0.38] disabled:shadow-none",
			colorClass,
			"overflow-hidden",
			typographyClass,
			loading && "pointer-events-none opacity-75 cursor-not-allowed",
			className,
		);

		const sharedTransition = customTransition ?? {
			borderRadius: BUTTON_RADIUS_SPRING,
			layout: BUTTON_SHAPE_MORPH_SPRING,
			default: SPRING_TRANSITION,
		};

		const activeIcon = isSelected && selectedIcon ? selectedIcon : icon;

		const innerContent = (
			<>
				{needsTouchTarget && <TouchTarget />}
				<Ripple ripples={ripples} onRippleDone={removeRipple} />

				<AnimatePresence initial={false} mode="wait">
					{(loading || (activeIcon && iconPosition === "leading")) && (
						<AnimatedIconSlot
							key={isSelected && selectedIcon ? "selected-icon" : "icon"}
							iconSize={sizeTokens.iconSize}
							ariaHidden={!loading}
							transition={sharedTransition}
						>
							{loading ? (
								loadingVariant === "loading-indicator" ? (
									<LoadingIndicator
										size={parseFloat(sizeTokens.iconSize) * 16}
										color="currentColor"
										aria-label="Loading"
									/>
								) : (
									<ProgressIndicator
										variant="circular"
										size={parseFloat(sizeTokens.iconSize) * 16}
										color="currentColor"
										trackColor="transparent"
										aria-label="Loading"
									/>
								)
							) : (
								activeIcon
							)}
						</AnimatedIconSlot>
					)}
				</AnimatePresence>

				{labelText && (
					<m.span
						layout="position"
						className="inline-flex items-center h-full gap-[inherit]"
						transition={sharedTransition}
					>
						{labelText}
					</m.span>
				)}

				<AnimatePresence initial={false} mode="wait">
					{!loading && activeIcon && iconPosition === "trailing" && (
						<AnimatedIconSlot
							key={isSelected && selectedIcon ? "selected-icon" : "icon"}
							iconSize={sizeTokens.iconSize}
							ariaHidden
							transition={sharedTransition}
						>
							{activeIcon}
						</AnimatedIconSlot>
					)}
				</AnimatePresence>
			</>
		);

		const handleAsChildPointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLElement>) => {
				springAnimate(motionRadius, pressedRadius);
				(onPointerDown as React.PointerEventHandler<HTMLElement>)?.(e);
			},
			[motionRadius, pressedRadius, onPointerDown],
		);

		const handleAsChildPointerUp = React.useCallback(() => {
			springAnimate(motionRadius, animateRadius);
		}, [motionRadius, animateRadius]);

		const handlePointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				onPointerDown(e);
			},
			[onPointerDown],
		);

		if (asChild) {
			const strippedProps = { ...restProps } as Record<string, unknown>;
			for (const key of MOTION_PROP_KEYS) delete strippedProps[key];
			const htmlProps =
				strippedProps as React.ButtonHTMLAttributes<HTMLButtonElement>;
			const child = React.Children.only(children) as React.ReactElement<{
				children?: React.ReactNode;
			}>;

			return (
				<LazyMotion features={domMax} strict>
					<Slot
						ref={mergedRef as React.Ref<HTMLButtonElement>}
						aria-label={computedAriaLabel}
						onClick={handleClick as React.MouseEventHandler<HTMLElement>}
						onPointerDown={handleAsChildPointerDown}
						onPointerUp={handleAsChildPointerUp}
						onPointerLeave={handleAsChildPointerUp}
						onPointerCancel={handleAsChildPointerUp}
						onKeyDown={handleKeyDown as React.KeyboardEventHandler<HTMLElement>}
						style={{
							...(mergedStyle as React.CSSProperties),
							borderRadius: `${animateRadius}px`,
						}}
						className={buttonClassName}
						{...htmlProps}
					>
						{React.cloneElement(child, { children: innerContent })}
					</Slot>
				</LazyMotion>
			);
		}

		return (
			<LazyMotion features={domMax} strict>
				<m.button
					ref={ref}
					type="button"
					aria-pressed={isToggle ? isSelected : undefined}
					aria-label={computedAriaLabel}
					aria-busy={loading ? true : undefined}
					aria-disabled={loading ? true : restProps.disabled}
					onClick={handleClick}
					onPointerDown={handlePointerDown}
					onKeyDown={handleKeyDown}
					style={mergedStyle}
					animate={{ borderRadius: animateRadius }}
					whileTap={{ borderRadius: pressedRadius }}
					transition={sharedTransition}
					className={buttonClassName}
					{...restProps}
				>
					{innerContent}
				</m.button>
			</LazyMotion>
		);
	},
);

ButtonComponent.displayName = "Button";

export const Button = React.memo(ButtonComponent);
